import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { updateFetcherData } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Text,
  useAlert,
  useToast
} from "@prismicio/editor-ui"
import { useState } from "react"
import { z } from "zod"

import { staticEditorConfig } from "../../app/editorConfig"
import { getAllReleases, type Release } from "../../app/services/release"

interface ReleaseDeleteButtonProps {
  release: Release | undefined
  size?: "small" | "medium" | "large"
}

export function ReleaseDeleteButton(props: ReleaseDeleteButtonProps) {
  const { release, size = "medium" } = props
  const [dialogOpen, setDialogOpen] = useState(false)

  if (!release) return null

  return (
    <>
      <Button
        size={size}
        startIcon="delete"
        color="grey"
        onClick={() => setDialogOpen(true)}
      >
        Delete
      </Button>
      <ReleaseMediaConfirmationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        release={release}
      />
    </>
  )
}

interface RemoveReleaseConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  release: Release
}

function ReleaseMediaConfirmationDialog(
  props: RemoveReleaseConfirmationDialogProps
) {
  const { open, onOpenChange, release } = props

  const [loading, setLoading] = useState(false)
  const { showAlert } = useAlert()
  const { showToast } = useToast()

  const releaseLabel = release.label || "..."

  function onRemoveMedia() {
    setLoading(true)

    removeRelease({ id: release.id })
      .then(() => {
        onOpenChange(false)
        updateFetcherData(getAllReleases, (body) => {
          // remove the release from the releases cache
          // the release page will handle the redirect to the upcoming releases page
          // when the release is deleted
          return body.filter((_release) => _release.id !== release.id)
        })
        showToast({
          title: "Release has been removed"
        })
      })
      .catch(() => {
        onOpenChange(false)
        showAlert({
          title: "Error",
          subtitle: `Something went wrong when removing ${releaseLabel}.`
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        icon="alert"
        title="Do you really want to delete this release?"
      />

      <DialogContent>
        <Box padding={16}>
          <Text>You are about to delete the release: {releaseLabel}.</Text>
        </Box>
      </DialogContent>

      <DialogActions
        asFooter
        ok={{
          onClick: onRemoveMedia,
          text: `Yes, I want to remove the release`,
          color: "tomato",
          loading
        }}
        cancel={{
          text: "No"
        }}
      />
    </Dialog>
  )
}

interface RemoveReleaseArgs {
  id: string
}

function removeRelease(args: RemoveReleaseArgs) {
  const { id } = args

  const config = staticEditorConfig

  if (!config.mediaLibrary) throw new Error("Media Library not configured.")

  const { authStrategy, coreApiBaseUrl } = config

  return request(new URL(`releases/${id}`, coreApiBaseUrl), {
    method: "DELETE",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown()
  })
}
